import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';
import MapaPicker from '../components/MapaPicker';

const EMPTY_FORM = {
    cliente_uuid: '',
    zona_id: '',
    tecnologia_id: '',
    plan_id: '',
    promocion_id: '',
    direccion_servicio: '',
    barrio_servicio: '',
    latitud: '',
    longitud: '',
    nodo_id: '',
    mikrotik_id: '',
    ip_pool_id: '',
    ip_asignada: '',
    olt_id: '',
    olt_puerto: '',
    olt_ont_id: '',
    olt_ont_serial: '',
    equipo_id: '',
    marquilla: '',
    fecha_instalacion: '',
    estado: 'pendiente',
    observaciones: ''
};

const ESTADO_COLOR = {
    pendiente:   'bg-yellow-100 text-yellow-700',
    activo:      'bg-green-100 text-green-700',
    suspendido:  'bg-red-100 text-red-600',
    retirado:    'bg-gray-100 text-gray-500'
};

const Contratos = () => {
    const { can, user } = useAuth();

    // Datos maestros
    const [clientes, setClientes] = useState([]);
    const [zonas, setZonas] = useState([]);
    const [tecnologias, setTecnologias] = useState([]);
    const [planes, setPlanes] = useState([]);
    const [promociones, setPromociones] = useState([]);
    const [nodos, setNodos] = useState([]);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [ipPools, setIpPools] = useState([]);
    const [olts, setOlts] = useState([]);
    const [equipos, setEquipos] = useState([]);
    const [mapKey, setMapKey] = useState('');

    // UI
    const [contratos, setContratos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [mapaOpen, setMapaOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [searchCliente, setSearchCliente] = useState('');

    // Cascada — filtros derivados
    const planesDisponibles = planes.filter(p =>
        (!form.zona_id || String(p.zona_id) === String(form.zona_id)) &&
        (!form.tecnologia_id || String(p.tecnologia_id) === String(form.tecnologia_id))
    );
    const mikrotiksFiltrados = mikrotiks.filter(m =>
        !form.nodo_id || String(m.nodo_id) === String(form.nodo_id)
    );
    const ipPoolsFiltrados = ipPools.filter(p =>
        !form.mikrotik_id || String(p.mikrotik_id) === String(form.mikrotik_id)
    );
    const oltsFiltradas = olts.filter(o =>
        !form.nodo_id || String(o.nodo_id) === String(form.nodo_id)
    );
    const tecnologiaSeleccionada = tecnologias.find(t => String(t.id) === String(form.tecnologia_id));
    const esFibra = tecnologiaSeleccionada?.nombre?.toLowerCase().includes('fibra');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [ctRes, clRes, zRes, tRes, pRes, prRes, nRes, mkRes, ipRes, oltRes, eqRes, mkeyRes] = await Promise.all([
                api.get('/contratos').catch(() => ({ data: [] })),
                api.get('/clientes'),
                api.get('/zonas'),
                api.get('/tecnologias'),
                api.get('/planes'),
                api.get('/promociones'),
                api.get('/infraestructura/nodos'),
                api.get('/infraestructura/mikrotik'),
                api.get('/infraestructura/ip-pool'),
                api.get('/infraestructura/olt'),
                api.get('/inventario/equipos'),
                api.get('/settings/map-key'),
            ]);
            setContratos(ctRes.data);
            setClientes(clRes.data);
            setZonas(zRes.data);
            setTecnologias(tRes.data);
            setPlanes(pRes.data);
            setPromociones(prRes.data);
            setNodos(nRes.data);
            setMikrotiks(mkRes.data);
            setIpPools(ipRes.data);
            setOlts(oltRes.data.filter(o => Number(o.activo)));
            setEquipos(eqRes.data);
            setMapKey(mkeyRes.data?.key || '');
        } catch (err) {
            setError('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => {
            const next = { ...prev, [name]: value };
            // Limpiar cascada cuando cambia zona o tecnología
            if (name === 'zona_id') { next.plan_id = ''; next.nodo_id = ''; next.mikrotik_id = ''; next.ip_pool_id = ''; next.olt_id = ''; }
            if (name === 'tecnologia_id') { next.plan_id = ''; next.olt_id = ''; next.olt_puerto = ''; next.olt_ont_id = ''; next.olt_ont_serial = ''; }
            if (name === 'nodo_id') { next.mikrotik_id = ''; next.ip_pool_id = ''; next.olt_id = ''; }
            if (name === 'mikrotik_id') { next.ip_pool_id = ''; }
            return next;
        });
    };

    const seleccionarCliente = (cliente) => {
        setClienteSeleccionado(cliente);
        setForm(prev => ({ ...prev, cliente_uuid: cliente.uuid }));
        setSearchCliente('');
    };

    const clientesFiltrados = searchCliente.length > 1
        ? clientes.filter(c =>
            c.nombre_completo?.toLowerCase().includes(searchCliente.toLowerCase()) ||
            c.documento?.includes(searchCliente)
          ).slice(0, 8)
        : [];

    const handleSubmit = async () => {
        if (!form.cliente_uuid || !form.zona_id || !form.tecnologia_id || !form.plan_id) {
            setError('Cliente, zona, tecnología y plan son requeridos');
            return;
        }
        setSaving(true); setError('');
        try {
            await api.post('/contratos', form);
            showSuccess('Contrato creado correctamente');
            setModalOpen(false);
            setForm(EMPTY_FORM);
            setClienteSeleccionado(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando contrato');
        } finally {
            setSaving(false);
        }
    };

    const filtered = contratos.filter(c =>
        `${c.cliente_nombre || ''} ${c.plan_nombre || ''} ${c.estado || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";
    const sectionTitle = (icon, label) => (
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <span>{icon}</span> {label}
        </p>
    );

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Contratos</h2>
                {can('contratos', 'create') && (
                    <button onClick={() => { setForm(EMPTY_FORM); setClienteSeleccionado(null); setModalOpen(true); }}
                        className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Crear contrato
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            {/* Buscador */}
            <div className="mb-4">
                <input type="text" placeholder="Buscar contrato..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
            </div>

            {/* Lista contratos */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron contratos</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Cliente</th>
                                <th className="px-4 py-3 text-left">Plan</th>
                                <th className="px-4 py-3 text-left">Zona</th>
                                <th className="px-4 py-3 text-left">Tecnología</th>
                                <th className="px-4 py-3 text-left">IP</th>
                                <th className="px-4 py-3 text-left">Estado</th>
                                <th className="px-4 py-3 text-left">Instalación</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(c => (
                                <tr key={c.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{c.cliente_nombre}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs">{c.plan_nombre}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{c.zona_nombre}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{c.tecnologia_nombre}</td>
                                    <td className="px-4 py-3 font-mono text-gray-500 text-xs">{c.ip_asignada || '—'}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLOR[c.estado] || ''}`}>
                                            {c.estado}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{c.fecha_instalacion || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal formulario contrato */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Nuevo contrato</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>

                        <div className="p-6 space-y-5">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            {/* PASO 1 — CLIENTE */}
                            <div className="border border-gray-200 rounded-xl p-4">
                                {sectionTitle('👤', 'Cliente')}
                                {!clienteSeleccionado ? (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Buscar por nombre o cédula..."
                                            value={searchCliente}
                                            onChange={e => setSearchCliente(e.target.value)}
                                            className={inputClass}
                                        />
                                        {clientesFiltrados.length > 0 && (
                                            <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto">
                                                {clientesFiltrados.map(c => (
                                                    <button key={c.uuid} onClick={() => seleccionarCliente(c)}
                                                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0">
                                                        <p className="font-medium text-gray-800">{c.nombre_completo}</p>
                                                        <p className="text-xs text-gray-500">{c.tipo_documento?.toUpperCase()} {c.documento} · {c.telefono}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                                        <div>
                                            <p className="font-medium text-blue-800">{clienteSeleccionado.nombre_completo}</p>
                                            <p className="text-xs text-blue-600">{clienteSeleccionado.tipo_documento?.toUpperCase()} {clienteSeleccionado.documento} · {clienteSeleccionado.telefono}</p>
                                        </div>
                                        <button onClick={() => { setClienteSeleccionado(null); setForm(prev => ({ ...prev, cliente_uuid: '' })); }}
                                            className="text-blue-400 hover:text-blue-600 text-sm">✕ Cambiar</button>
                                    </div>
                                )}
                            </div>

                            {/* PASO 2 — PLAN COMERCIAL (solo si hay cliente) */}
                            {clienteSeleccionado && (
                                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                    {sectionTitle('📦', 'Plan comercial')}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Zona *</label>
                                            <select name="zona_id" value={form.zona_id} onChange={handleChange} className={inputClass}>
                                                <option value="">Seleccionar zona...</option>
                                                {zonas.filter(z => Number(z.activa)).map(z => (
                                                    <option key={z.id} value={z.id}>{z.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Tecnología *</label>
                                            <select name="tecnologia_id" value={form.tecnologia_id} onChange={handleChange} className={inputClass} disabled={!form.zona_id}>
                                                <option value="">Seleccionar tecnología...</option>
                                                {tecnologias.filter(t => Number(t.activa)).map(t => (
                                                    <option key={t.id} value={t.id}>{t.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Plan *</label>
                                            <select name="plan_id" value={form.plan_id} onChange={handleChange} className={inputClass} disabled={!form.tecnologia_id}>
                                                <option value="">Seleccionar plan...</option>
                                                {planesDisponibles.map(p => (
                                                    <option key={p.id} value={p.id}>{p.nombre} — ${Number(p.cargo_fijo_mensual).toLocaleString()}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Promoción</label>
                                            <select name="promocion_id" value={form.promocion_id} onChange={handleChange} className={inputClass}>
                                                <option value="">Sin promoción</option>
                                                {promociones.filter(p => Number(p.activa)).map(p => (
                                                    <option key={p.id} value={p.id}>{p.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PASO 3 — UBICACIÓN DEL SERVICIO (solo si hay plan) */}
                            {form.plan_id && (
                                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                    {sectionTitle('📍', 'Ubicación del servicio')}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Dirección del servicio</label>
                                            <input name="direccion_servicio" value={form.direccion_servicio} onChange={handleChange} className={inputClass} placeholder="Ej: Cra 5 #5-108" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Barrio</label>
                                            <input name="barrio_servicio" value={form.barrio_servicio} onChange={handleChange} className={inputClass} placeholder="Ej: Bolo San Isidro" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Coordenadas <span className="text-gray-400 font-normal">(opcional — se puede agregar después)</span></label>
                                        <div className="flex gap-2 mb-2">
                                            <input name="latitud" value={form.latitud} onChange={handleChange} className={inputClass} placeholder="Latitud" />
                                            <input name="longitud" value={form.longitud} onChange={handleChange} className={inputClass} placeholder="Longitud" />
                                            <button type="button" onClick={() => setMapaOpen(true)}
                                                className="px-3 py-2 border border-blue-300 text-blue-600 rounded-lg text-sm hover:bg-blue-50 whitespace-nowrap">
                                                📍 Mapa
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PASO 4 — INFRAESTRUCTURA (solo si hay plan) */}
                            {form.plan_id && (
                                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                    {sectionTitle('🖥️', 'Infraestructura')}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Nodo</label>
                                            <select name="nodo_id" value={form.nodo_id} onChange={handleChange} className={inputClass}>
                                                <option value="">Seleccionar nodo...</option>
                                                {nodos.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>MikroTik</label>
                                            <select name="mikrotik_id" value={form.mikrotik_id} onChange={handleChange} className={inputClass} disabled={!form.nodo_id}>
                                                <option value="">Seleccionar MikroTik...</option>
                                                {mikrotiksFiltrados.map(m => <option key={m.id} value={m.id}>{m.etiqueta}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>IP Pool</label>
                                            <select name="ip_pool_id" value={form.ip_pool_id} onChange={handleChange} className={inputClass} disabled={!form.mikrotik_id}>
                                                <option value="">Seleccionar pool...</option>
                                                {ipPoolsFiltrados.map(p => <option key={p.id} value={p.id}>{p.nombre} ({p.subnet}/{p.prefijo})</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>IP asignada</label>
                                            <input name="ip_asignada" value={form.ip_asignada} onChange={handleChange} className={inputClass} placeholder="Ej: 192.168.51.45" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PASO 5 — OLT (solo si es fibra) */}
                            {form.plan_id && esFibra && (
                                <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 space-y-3">
                                    {sectionTitle('📡', 'OLT — Fibra óptica')}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>OLT</label>
                                            <select name="olt_id" value={form.olt_id} onChange={handleChange} className={inputClass}>
                                                <option value="">Seleccionar OLT...</option>
                                                {oltsFiltradas.map(o => <option key={o.id} value={o.id}>{o.etiqueta} — {o.modelo}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Puerto PON</label>
                                            <input name="olt_puerto" value={form.olt_puerto} onChange={handleChange} className={inputClass} placeholder="Ej: 0/0/1" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>ONT ID</label>
                                            <input name="olt_ont_id" value={form.olt_ont_id} onChange={handleChange} className={inputClass} placeholder="Ej: 5" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Serial ONT</label>
                                            <input name="olt_ont_serial" value={form.olt_ont_serial} onChange={handleChange} className={inputClass} placeholder="Ej: 48575443..." />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PASO 6 — EQUIPO */}
                            {form.plan_id && (
                                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                    {sectionTitle('🔧', 'Equipo en comodato')}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Equipo</label>
                                            <select name="equipo_id" value={form.equipo_id} onChange={handleChange} className={inputClass}>
                                                <option value="">Seleccionar equipo...</option>
                                                {equipos.map(e => <option key={e.id} value={e.id}>{e.marca_nombre} {e.modelo_nombre} — {e.serial}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Marquilla / Tag</label>
                                            <input name="marquilla" value={form.marquilla} onChange={handleChange} className={inputClass} placeholder="Ej: 00025" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PASO 7 — INSTALACIÓN */}
                            {form.plan_id && (
                                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                    {sectionTitle('📅', 'Instalación y estado')}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Fecha de instalación</label>
                                            <input type="date" name="fecha_instalacion" value={form.fecha_instalacion} onChange={handleChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Estado</label>
                                            <select name="estado" value={form.estado} onChange={handleChange} className={inputClass}>
                                                <option value="pendiente">Pendiente instalación</option>
                                                <option value="activo">Activo</option>
                                                <option value="suspendido">Suspendido</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Observaciones</label>
                                        <textarea name="observaciones" value={form.observaciones} onChange={handleChange}
                                            className={inputClass} rows={3} placeholder="Notas adicionales..." />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Botones */}
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                Cancelar
                            </button>
                            <button onClick={handleSubmit} disabled={saving || !form.plan_id}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Guardando...' : 'Crear contrato'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal mini mapa */}
            {mapaOpen && mapKey && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">📍 Ubicar en el mapa</h3>
                            <button onClick={() => setMapaOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-4">
                            <MapaPicker
                                apiKey={mapKey}
                                latitud={form.latitud}
                                longitud={form.longitud}
                                onChange={(lat, lng) => {
                                    setForm(prev => ({
                                        ...prev,
                                        latitud: lat ? lat.toFixed(7) : '',
                                        longitud: lng ? lng.toFixed(7) : ''
                                    }));
                                }}
                            />
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setMapaOpen(false)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                Cancelar
                            </button>
                            <button onClick={() => setMapaOpen(false)}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                ✓ Confirmar ubicación
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Contratos;
